<template>
  <v-card
    :class="{
      'ma-6': !xs,
      'alert-history-card': true,
      'fit-to-space': !isMobile,
      'is-mobile': isMobile
    }"
  >
    <v-card-title class="header-title pa-4">
      {{ $t('alertHistory.title') }}
    </v-card-title>
    <v-divider class="divider"></v-divider>
    <v-data-table
      v-if="!$auth.loading && alertHistory !== null && alertHistory.length !== 0"
      :headers="headers"
      :items="alertHistory"
      :mobile-breakpoint="thresholds.md"
      :class="{ mobile: isMobile }"
      :search="customTableFilter"
      @update:currentItems="setItems($event)"
      show-select
      :options="tableOptions"
      v-model:sort-by="sortedBy"
      v-model:itemsPerPage="itemsPerPage"
      :items-per-page-options="[10, 25, 50]"
      :items-per-page-text="isMobile ? 'tables.rows' : 'tables.rowsPerPage'"
      fixed-header
      fixed-footer
    >
      <template v-if="isMobile" v-slot:[`body.prepend`]>
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center px-4 py-2">
            <v-checkbox
              class="mobile-select-checkbox"
              color="primary"
              :model-value="isAllCurrentSelected"
              :indeterminate="isSomeCurrentSelected"
              @update:modelValue="toggleSelectAllCurrent"
              hide-details
              data-cy="checkboxMobileSelectAll"
            />
            <span class="ml-3">{{ $t('alertHistory.selectAll') }}</span>
          </div>
        </div>
        <v-divider />
      </template>
      <!-- top slot above the table, renders with it -->
      <template v-slot:top>
        <!-- search -->
        <div class="table-bar" v-if="!selected.length">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            :label="$t('alertHistory.search')"
            single-line
            hide-details
            class="search-field"
            data-cy="searchField"
          />
          <span class="export">
            <ExportNotifications />
          </span>
        </div>
        <!-- global delete -->
        <div class="table-bar" :class="{ 'delete-mode': !!selected.length }" v-else>
          <span class="selection-message" data-cy="deleteText">
            {{ $t('alertHistory.selectionMessage', { value: selected.length }) }}
          </span>
          <v-spacer />
          <!-- delete confirm -->
          <FusionBaseDialog
            :open="deleteOpen"
            @close="deleteClose()"
            :title="$t('alertHistory.deleteDialog.title', { plurality })"
            :persistentOverlay="true"
            icon="mdi-delete"
            padded
          >
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                class="alert-delete"
                variant="text"
                prepend-icon="mdi-delete"
                @click.stop="deleteOpen = true"
                data-cy="deleteButton"
              >
                {{ $t('buttons.delete') }}
              </v-btn>
            </template>
            <div class="pa-4">
              {{
                $t('alertHistory.deleteDialog.message', {
                  value: selected.length,
                  plurality
                })
              }}
            </div>
            <template v-slot:actions>
              <v-btn
                @click="deleteClose()"
                variant="outlined"
                class="text-black"
                data-cy="deleteCancelButtonModal"
                :disabled="deleteInProgress"
                >{{ $t('buttons.cancel') }}</v-btn
              >
              <v-spacer></v-spacer>
              <v-btn
                @click="deleteNotifications()"
                class="bg-primary text-white"
                variant="text"
                data-cy="deleteButtonModal"
                :loading="deleteInProgress"
                >{{ $t('buttons.delete') }}</v-btn
              >
            </template>
          </FusionBaseDialog>
        </div>
      </template>
      <!-- header slot -->
      <template v-slot:[`header.data-table-select`]>
        <v-checkbox
          class="select-checkbox"
          color="primary"
          :model-value="isAllCurrentSelected"
          :indeterminate="isSomeCurrentSelected"
          @update:modelValue="toggleSelectAllCurrent"
          data-cy="checkboxDesktop"
          hide-details
        />
      </template>
      <!-- item slot -->
      <template v-slot:item="{ item }">
        <!-- desktop row -->
        <tr v-if="!isMobile" class="alert-history-row">
          <td class="select-cell">
            <v-checkbox
              class="select-checkbox ml-n2"
              color="primary"
              v-model="selected"
              :value="item.id"
              hide-details
            />
          </td>
          <td class="col-title" data-cy="titleColumnData">
            {{ item.title }}
          </td>
          <td class="col-triggered-on" data-cy="triggeredOnData">
            {{ item.triggeredAt }}
          </td>
          <td class="col-device" data-cy="deviceColumnData">
            <v-tooltip location="bottom" :max-width="600" :disabled="!isNameOverflowing[item.id]">
              <template v-slot:activator="{ props }">
                <div v-bind="props" class="device-name" :ref="item.id">
                  <span>{{ item.deviceName }}</span>
                </div>
              </template>
              <span>{{ item.deviceName }}</span>
            </v-tooltip>
          </td>
          <td class="col-location" data-cy="locationColumnData">
            <v-tooltip
              v-if="item.deviceLocationName"
              location="bottom"
              :max-width="600"
              :disabled="!isNameOverflowing[item.id]"
            >
              <template v-slot:activator="{ props }">
                <div v-bind="props" class="device-location" :ref="item.id">
                  <span>{{ item.deviceLocationName }}</span>
                </div>
              </template>
              <span>{{ item.deviceLocationName }}</span>
            </v-tooltip>
            <span v-else class="not-available-text">
              {{ $t('alertHistory.notAvailable') }}
            </span>
          </td>
          <td class="col-model" data-cy="modelColumnData">
            {{ item.deviceModel }}
          </td>
          <td class="col-condition" data-cy="conditionColumnData">
            {{ item.condition }}
          </td>
          <td class="col-message" data-cy="messageColumnData">
            {{ item.message }}
          </td>
          <td class="col-alert-level" data-cy="alertLevelColumnData">
            {{ item.severity }}
          </td>
        </tr>
        <!-- mobile row -->
        <tr v-else>
          <td class="mobile-list" colspan="9">
            <v-row no-gutters>
              <v-col cols="auto" class="mobile-select-col">
                <v-checkbox
                  class="mobile-select-checkbox"
                  color="primary"
                  v-model="selected"
                  :value="item.id"
                  hide-details
                />
              </v-col>
              <v-col>
                <ul class="flex-content">
                  <li class="flex-item item-title">{{ item.title }}</li>
                  <li class="flex-item">
                    <span> {{ $t('alertHistory.tableHeaders.triggeredOn') }}:&nbsp; </span>
                    <span>
                      {{ new Date(item.triggeredAt).toLocaleString() }}
                    </span>
                  </li>
                  <li class="flex-item">
                    <span> {{ $t('alertHistory.tableHeaders.deviceName') }}:&nbsp; </span>
                    <v-tooltip
                      location="bottom"
                      :max-width="600"
                      :disabled="!isNameOverflowing[item.id]"
                    >
                      <template v-slot:activator="{ props }">
                        <span v-bind="props" class="device-name" :ref="item.id">
                          <span>{{ item.deviceName }}</span>
                        </span>
                      </template>
                      <span>{{ item.deviceName }}</span>
                    </v-tooltip>
                  </li>
                  <li class="flex-item">
                    <span> {{ $t('alertHistory.tableHeaders.deviceLocationName') }}:&nbsp; </span>
                    <v-tooltip
                      v-if="item.deviceLocationName"
                      location="bottom"
                      :max-width="600"
                      :disabled="!isNameOverflowing[item.id]"
                    >
                      <template v-slot:activator="{ props }">
                        <span v-bind="props" class="device-location" :ref="item.id">
                          <span>{{ item.deviceLocationName }}</span>
                        </span>
                      </template>
                      <span>{{ item.deviceLocationName }}</span>
                    </v-tooltip>
                    <span v-else class="not-available-text">
                      {{ $t('alertHistory.notAvailable') }}
                    </span>
                  </li>
                  <li class="flex-item">
                    <span> {{ $t('alertHistory.tableHeaders.deviceModel') }}:&nbsp; </span>
                    <span>{{ item.deviceModel }}</span>
                  </li>
                  <li class="flex-item">
                    <span> {{ $t('alertHistory.tableHeaders.condition') }}:&nbsp; </span>
                    <span>{{ item.condition }}</span>
                  </li>
                  <li class="flex-item">
                    <span> {{ $t('alertHistory.tableHeaders.message') }}:&nbsp; </span>
                    <span>{{ item.message }}</span>
                  </li>
                  <li class="flex-item">
                    <span> {{ $t('alertHistory.tableHeaders.alertLevel') }}:&nbsp; </span>
                    <span>{{ item.severity }}</span>
                  </li>
                </ul>
              </v-col>
            </v-row>
          </td>
        </tr>
      </template>
    </v-data-table>
    <!-- empty message -->
    <div
      v-else-if="!$auth.loading && alertHistory !== null && alertHistory.length === 0"
      class="text-center emptyMessage"
    >
      {{ $t('alertHistory.emptyMessage') }}
    </div>
    <!-- progress spinner -->
    <div v-else class="text-center emptyMessage">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue'
import { useDisplay } from 'vuetify'
import type { DataTableHeader, DataTableSortItem } from 'vuetify'
import debounce from 'lodash/debounce'
import { AuthEvents, AuthEventsBus } from '@fusion/auth-v3'
import type { Notification } from '@/types/alerts'
import ExportNotifications from '@/components/dialogs/ExportAlertsDialog.vue'
import { deviceModelDisplay, deviceSubmodelDisplay, ProductFamily } from '@/services/map'
import { isChildOverflowing } from '@/services/html-utilities'

const LOCAL_STORAGE_KEYS = 'tsi.alertHistoryTableSettings'

interface AlertHistoryRow {
  id: string
  title: string
  triggeredAt: string
  deviceName: string
  deviceLocationName: string
  deviceModel: string
  userMessage: string
  severity: string
  message: string
  condition: string
}

type DataTableCurrentItem = AlertHistoryRow | { raw?: AlertHistoryRow; id?: string }

export default defineComponent({
  name: 'AlertHistory',
  setup() {
    const { xs, smAndDown, thresholds } = useDisplay()
    return { xs, smAndDown, thresholds }
  },
  components: { ExportNotifications },
  mounted() {
    if (this.$auth.isAuthenticated) {
      this.getAlertNotifications()
    }

    AuthEventsBus.$on(AuthEvents.AUTH_CHANGED, async (isAuthenticated: boolean) => {
      if (isAuthenticated) {
        this.getAlertNotifications()
      }
    })

    this.observe(this.$el)
    this.getAlertHistoryTableSettings()
  },
  updated() {
    this.setIsNameOverflowing()
  },
  unmounted() {
    if (this.observer) this.observer.disconnect()
  },
  data() {
    return {
      sortedBy: [{ key: 'triggeredAt', order: 'desc' }] as DataTableSortItem[],
      itemsPerPage: 10,
      tableOptions: {} as Record<string, unknown>,
      selected: [] as string[],
      currentItems: [] as AlertHistoryRow[],
      deleteOpen: false,
      deleteInProgress: false,
      search: '',
      headers: [
        {
          title: this.$t('alertHistory.tableHeaders.title'),
          align: 'start',
          key: 'title'
        },
        {
          title: this.$t('alertHistory.tableHeaders.triggeredOn'),
          key: 'triggeredAt',
          align: 'start',
          sort: (a: string, b: string) => {
            // turn strings into dates, subtract them to get a value that is either negative, positive, or zero
            return new Date(a).getTime() - new Date(b).getTime()
          }
        },
        {
          title: this.$t('alertHistory.tableHeaders.deviceName'),
          key: 'deviceName',
          align: 'start'
        },
        {
          title: this.$t('alertHistory.tableHeaders.deviceLocationName'),
          key: 'deviceLocationName',
          align: 'start'
        },
        {
          title: this.$t('alertHistory.tableHeaders.deviceModel'),
          key: 'deviceModel',
          align: 'start'
        },
        {
          title: this.$t('alertHistory.tableHeaders.condition'),
          key: 'condition',
          align: 'start'
        },
        {
          title: this.$t('alertHistory.tableHeaders.message'),
          key: 'message',
          align: 'start'
        },
        {
          title: this.$t('alertHistory.tableHeaders.alertLevel'),
          key: 'severity',
          align: 'start'
        }
      ] as DataTableHeader<AlertHistoryRow>[],
      alertHistoryData: null as Notification[] | null,
      isNameOverflowing: {} as Record<string, boolean>,
      observer: null as ResizeObserver | null
    }
  },
  computed: {
    alertHistory(): AlertHistoryRow[] | null {
      // Submodel is not returned with the alert data
      // and may not be yet available in Vuex at mount.
      // Storing the raw data and returning the modified results
      // in a computed property allows the dataset to be reactive.
      return this.alertHistoryData ? this.updateAlertHistory(this.alertHistoryData) : null
    },
    isMobile(): boolean {
      return this.smAndDown
    },
    customTableFilter(): string {
      if (this.search.length >= 2) {
        return this.search
      } else if (this.search.length === 1) {
        return '//no-result//'
      }
      return ''
    },
    currentItemIds(): string[] {
      return this.currentItems.map((item) => item.id)
    },
    displayedSelectedCount(): number {
      if (!this.currentItemIds.length || !this.selected.length) {
        return 0
      }

      const selectedIds = new Set(this.selected)
      return this.currentItemIds.filter((id) => selectedIds.has(id)).length
    },
    isAllCurrentSelected(): boolean {
      return (
        this.currentItemIds.length > 0 && this.displayedSelectedCount === this.currentItemIds.length
      )
    },
    isSomeCurrentSelected(): boolean {
      return this.displayedSelectedCount > 0 && !this.isAllCurrentSelected
    },
    plurality(): string {
      return this.selected.length > 0 ? 's' : ''
    }
  },
  watch: {
    currentItems(): void {
      this.$forceUpdate()
    },
    sortedBy: {
      deep: true,
      handler(): void {
        this.setAlertHistoryTableSettings()
      }
    },
    itemsPerPage(): void {
      this.setAlertHistoryTableSettings()
    }
  },
  methods: {
    setItems(items: DataTableCurrentItem[]) {
      // Vuetify may emit wrapped table items here (for example { raw: item }).
      // Normalize to the row shape used by the checkbox values.
      this.currentItems = items
        .map((item) => ('raw' in item && item.raw ? item.raw : (item as AlertHistoryRow)))
        .filter((item) => !!item?.id)
      this.selected = [...this.selected]
    },
    toggleSelectAllCurrent(value: boolean | null): void {
      const currentIds = this.currentItemIds
      if (!currentIds.length) {
        return
      }

      if (value) {
        const merged = new Set(this.selected)
        currentIds.forEach((id) => merged.add(id))
        this.selected = Array.from(merged)
        return
      }

      const visibleIds = new Set(currentIds)
      this.selected = this.selected.filter((id) => !visibleIds.has(id))
    },
    async getAlertNotifications(): Promise<void> {
      const res = await this.$api.getAlertNotifications()
      if (res.ok) {
        const body: Notification[] = await res.json()
        this.alertHistoryData = body
      }
    },
    updateAlertHistory(alertHistoryData: Notification[]): AlertHistoryRow[] {
      const alertHistory: AlertHistoryRow[] = []

      if (alertHistoryData && alertHistoryData.length > 0) {
        // reverse the data set, so that the newest alerts are at the top of the table
        for (const notification of alertHistoryData.slice().reverse()) {
          const newAlert: AlertHistoryRow = {
            id: notification.id,
            title: notification.trigger.title,
            triggeredAt: notification.triggeredAt,
            deviceName: notification.device.name,
            deviceLocationName: notification.device.locationName,
            deviceModel: '',
            userMessage: notification.trigger.userMessage ?? '',
            severity: notification.trigger.severity ?? '',
            message: notification.message,
            condition: ''
          }

          // translate the severity
          newAlert.severity = this.$t(`map.deviceAlerts.fields.levels.${newAlert.severity}`)

          newAlert.triggeredAt = new Date(newAlert.triggeredAt).toLocaleString()

          // build device model+submodel string
          const modelKey = this.$store.getters['devicemodels/getModelKey'](
            notification.device.model
          )
          let deviceModel = deviceModelDisplay(modelKey, this.$t.bind(this))
          const submodel = this.$store.getters['devices/getSubmodelByDeviceId'](
            notification.device.id
          )
          const submodelKey = this.$store.getters['devicemodels/getSubmodelKey'](
            notification.device.model,
            submodel
          )
          const deviceSubmodel = deviceSubmodelDisplay(submodelKey, this.$t.bind(this))
          if (deviceSubmodel) deviceModel += ` (${deviceSubmodel})`
          newAlert.deviceModel = deviceModel

          // build condition string
          const productFamily = this.$store.getters['devicemodels/getModelProductFamily'](
            notification.device.model
          )

          const conditionOperator = notification.trigger.condition?.operator
          const conditionValue = notification.trigger.condition?.value
          if (conditionValue != 0 && !conditionValue) {
            newAlert.condition = this.$t('map.deviceAlerts.fields.conditions.none')
          } else if (
            productFamily !== ProductFamily.BLUESKY &&
            productFamily !== ProductFamily.AIRASSURE
          ) {
            newAlert.condition = this.$t(
              `map.deviceAlerts.fields.${productFamily}Conditions.${notification.trigger.condition?.field}`
            )
          } else {
            const conditionMeasurement = this.$t(
              `map.deviceAlerts.fields.sensors.${notification.trigger.condition?.field}`
            )
            newAlert.condition = this.$t(
              `map.deviceAlerts.fields.conditions.${conditionOperator}`,
              { measurement: conditionMeasurement, value: conditionValue }
            )
          }

          alertHistory.push(newAlert)
        }
      }

      return alertHistory
    },
    deleteClose(): void {
      this.selected = []
      this.deleteOpen = false
    },
    async deleteNotifications(): Promise<void> {
      if (!this.deleteInProgress) {
        this.deleteInProgress = true
        const notes = [...this.selected]
        const noteMap = notes.map((note) => this.$api.deleteAlertNotification(note))

        await Promise.all(noteMap)
        this.getAlertNotifications()
        this.deleteClose()
        this.deleteInProgress = false
      }
    },
    async setIsNameOverflowing(): Promise<void> {
      await nextTick()
      this.currentItems.forEach((item: AlertHistoryRow) => {
        this.isNameOverflowing[item.id] = isChildOverflowing(this.$refs[item.id] as HTMLElement)
      })
    },
    observe(el: Element | null) {
      const forceUpdate = debounce(() => {
        this.$forceUpdate()
      }, 200)

      this.observer = new ResizeObserver(forceUpdate)
      if (el instanceof Element) {
        this.observer.observe(el)
      }
    },
    setAlertHistoryTableSettings() {
      const save = {
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortedBy
      }
      localStorage.setItem(LOCAL_STORAGE_KEYS, JSON.stringify(save))
    },
    getAlertHistoryTableSettings() {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS)
      if (raw === null) return
      try {
        const settings = JSON.parse(raw)
        if (settings.itemsPerPage) {
          this.itemsPerPage = settings.itemsPerPage
        }
        if (Array.isArray(settings.sortBy) && settings.sortBy.length > 0) {
          if (typeof settings.sortBy[0] === 'string') {
            // Old format: sortBy is string[], sortDesc is boolean[] — migrate to DataTableSortItem[]
            const sortDesc: boolean[] = Array.isArray(settings.sortDesc) ? settings.sortDesc : []
            this.sortedBy = settings.sortBy.map((key: string, i: number) => ({
              key,
              order: (sortDesc[i] ? 'desc' : 'asc') as 'asc' | 'desc'
            }))
          } else {
            // New format: sortBy is DataTableSortItem[]
            this.sortedBy = settings.sortBy as DataTableSortItem[]
          }
        }
      } catch {
        // Ignore malformed localStorage data, use defaults
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.alert-history-card {
  .header-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 300;
  }

  .divider {
    margin-left: 16px;
    margin-right: 16px;
    margin-bottom: 24px;
  }

  .emptyMessage {
    height: 240px;
    line-height: 240px;
    color: rgba(0, 0, 0, 0.56);
  }

  .alert-history-row td {
    vertical-align: middle;
  }

  :deep(.select-checkbox .v-selection-control) {
    justify-content: center;
  }

  .device-name,
  .device-location {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mobile-select-checkbox {
    margin-left: -8px;
  }

  td.mobile-list {
    width: 100vw;
    max-width: 100vw;

    .mobile-select-col {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 4px;
    }

    .ma-6 & {
      width: calc(100vw - 48px);
      max-width: calc(100vw - 48px);
    }
    ul {
      width: 100%;
      padding-top: 16px;
      padding-bottom: 16px;
      padding-left: 12px;
      list-style-type: none;
      .item-title {
        font-weight: 500;
        font-size: 15px;
        margin: 4px 0;
      }
    }
    .flex-item {
      display: flex;
      flex-wrap: wrap;
      margin-bottom: 4px;
      > span:first-child {
        display: inline-block;
        margin-right: 4px;
        white-space: nowrap;
        color: rgba(0, 0, 0, 0.6);
      }
      > span:last-child {
        display: inline-block;
        &.device-name,
        &.device-location {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .mobile-select-all-row {
    .mobile-select-all {
      display: flex;
      align-items: center;
    }
  }

  .table-bar {
    font-size: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;

    background-color: transparent;
    margin-left: 16px;
    margin-right: 16px;
    margin-bottom: 16px;
    min-height: 48px;

    .search-field {
      max-width: 50%;
    }

    &.delete-mode {
      width: 100%;
      margin-left: 0;
      padding-right: 16px;
      background-color: var(--tsi-color--main-primary);
      transition: height 0.5s linear;

      border-radius: 2px;
    }

    .selection-message {
      color: white;
      padding: 6px;
      margin: 6px 6px;
    }

    .alert-delete {
      color: white;
      padding-top: 6px;
      padding-bottom: 6px;
      margin: 6px 0;
    }
  }

  :deep(.v-data-table-footer) {
    padding: 8px 16px;
  }

  .col-title {
    min-width: 80px;
    max-width: 120px;
  }

  .col-triggered-on {
    min-width: 130px;
  }

  .col-device,
  .col-location {
    min-width: 150px;
    max-width: 200px;
  }

  .col-model {
    max-width: 160px;
  }

  .col-condition,
  .col-message {
    min-width: 110px;
    max-width: 220px;
  }

  .col-alert-level {
    min-width: 110px;
  }
}
.alert-history-card.is-mobile {
  .search-field {
    max-width: 100%;
  }
  :deep(.v-data-table__td-sort-select .v-input__append) {
    display: none !important;
  }
}
</style>
